import { HttpContext, HttpContextToken, HttpErrorResponse } from '@angular/common/http';

/**
 * Marks a request whose CALLER handles a network failure by falling back to the offline catalogue
 * (`IngestApiService`'s browse / games / resolve / manifest calls). The interceptor then skips its
 * generic `HTTP «status»` snack for a status-0 failure: the caller is about to answer from IndexedDB,
 * and a toast saying "HTTP 0" over a page that renders fine would be noise the user cannot act on.
 * Every other status still takes the interceptor's normal path.
 */
export const OFFLINE_FALLBACK = new HttpContextToken<boolean>(() => false);

export function offlineFallbackContext(): HttpContext {
  return new HttpContext().set(OFFLINE_FALLBACK, true);
}

/**
 * Marks a request whose CALLER says every failure itself, inline and with its own "Riprova" (Scan's
 * strict card reads: "Aggiorna prezzi", the add sheet, the desk panel, the card's Prezzi tab). The
 * interceptor then raises NO generic `HTTP «status»` snack for any status and does not bounce a 403
 * home: a list of 50 cards with the server in 503 raised ~100 snacks over the line that already said
 * "N non lette per un errore del server" (review round 9). A 401 still refreshes and retries.
 */
export const CALLER_SHOWS_ERRORS = new HttpContextToken<boolean>(() => false);

/** `context` with {@link CALLER_SHOWS_ERRORS} set when `strict` (a new context when none). */
export function strictReadContext(strict: boolean | undefined, context?: HttpContext): HttpContext | undefined {
  return strict ? (context ?? new HttpContext()).set(CALLER_SHOWS_ERRORS, true) : context;
}

/**
 * The error the interceptor answers with, WITHOUT touching the network, for an API call made while
 * the tab is in offline mode. Status 0, like a real network failure, so every caller's existing
 * "it did not get through" handling applies unchanged.
 */
export function offlineModeError(url: string): HttpErrorResponse {
  return new HttpErrorResponse({
    status: 0,
    statusText: 'Offline mode',
    url,
    error: new ProgressEvent('error'),
  });
}

/**
 * Marks a BACKGROUND DOWNLOAD of offline data (Intelligence > Scan's "Modalita' offline": the unified
 * manifest, the scan quotes snapshot and its parts, the printings catalogue parts it fetches for a
 * game). Nobody is waiting on such a request: the offline screen shows its outcome per game and the
 * download retries by itself. So the interceptor
 *  - raises NO generic snack for any status (a 304 "not modified" answer is an error to HttpClient,
 *    a 5xx is retried, a 403 means the role may not read that piece) and, for a 403, does NOT
 *    navigate home -- a download running behind the Scan screens must not throw the user out of them;
 *  - still refreshes and retries on a 401 (an access token expiring mid-download is ordinary), but
 *    never ENDS the session when that refresh fails: the session dies on the user's next real call,
 *    as for the push watchers (`pushNoTeardownEndpoints`).
 * Offline mode still refuses these requests locally like any other.
 */
export const OFFLINE_DOWNLOAD = new HttpContextToken<boolean>(() => false);

/** The context of an offline-data download: {@link OFFLINE_DOWNLOAD}, plus {@link OFFLINE_FALLBACK}. */
export function offlineDownloadContext(): HttpContext {
  return new HttpContext().set(OFFLINE_DOWNLOAD, true).set(OFFLINE_FALLBACK, true);
}

/**
 * The longest one offline-data request (a manifest, one part) may take before it is abandoned with
 * a `TimeoutError`: the download fails like any other failure and the round's retry asks again. A
 * request that never answers otherwise held the round, the app-update gate and "Annulla" with it, for
 * ever (review 25/10/2026). Generous: a part is a few MB, on a slow mobile network too.
 */
export const OFFLINE_DOWNLOAD_TIMEOUT_MS = 120_000;
